import { useMemo } from "react"
import { useRouter } from "next/router"

import { useAuth, useOrganizationList } from "@clerk/nextjs"

import { createBillingClient, type BillingClient } from "../client"

type ClerkGetToken = (options?: {
  organizationId?: string
}) => Promise<string | null>

interface BillingClientContext {
  client: BillingClient
  isReady: boolean
  organizationSlug: string | null
  routeOrganizationMissing: boolean
}

function getRouteOrganizationSlug(querySlug: unknown): string | null {
  return typeof querySlug === "string" && querySlug.length > 0
    ? querySlug
    : null
}

function getBillingToken({
  getToken,
  organizationId,
}: {
  getToken: ClerkGetToken
  organizationId: string | null
}) {
  return organizationId ? getToken({ organizationId }) : getToken()
}

export function useBillingClientContext(): BillingClientContext {
  const { getToken, orgId, orgSlug } = useAuth()
  const { isLoaded: membershipsLoaded, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  })
  const router = useRouter()

  const routeOrganizationSlug = getRouteOrganizationSlug(router.query.slug)
  const routeOrganizationMembership = useMemo(
    () =>
      routeOrganizationSlug
        ? userMemberships?.data?.find(
            (membership) =>
              membership.organization.slug === routeOrganizationSlug,
          )
        : undefined,
    [routeOrganizationSlug, userMemberships?.data],
  )
  const activeOrganizationIsRoute =
    routeOrganizationSlug !== null &&
    orgSlug === routeOrganizationSlug &&
    orgId != null
  const billingOrganizationId = routeOrganizationSlug
    ? (routeOrganizationMembership?.organization.id ??
      (activeOrganizationIsRoute ? orgId : null))
    : (orgId ?? null)
  const billingOrganizationSlug = routeOrganizationSlug ?? orgSlug ?? null
  const routeOrganizationMissing =
    routeOrganizationSlug !== null &&
    membershipsLoaded &&
    !userMemberships?.hasNextPage &&
    !activeOrganizationIsRoute &&
    routeOrganizationMembership === undefined
  const isReady =
    routeOrganizationSlug === null ||
    activeOrganizationIsRoute ||
    (membershipsLoaded &&
      (billingOrganizationId !== null || routeOrganizationMissing))

  const client = useMemo(
    () =>
      createBillingClient({
        getToken: () =>
          getBillingToken({
            getToken,
            organizationId: billingOrganizationId,
          }),
      }),
    [billingOrganizationId, getToken],
  )

  return {
    client,
    isReady,
    organizationSlug: billingOrganizationSlug,
    routeOrganizationMissing,
  }
}

export function useBillingClient(): BillingClient {
  return useBillingClientContext().client
}
